/**
 * CollapsibleVideoPlayer Component
 * ~~~***The Video Peekaboo Box***~~~
 * 
 * Purpose: A reusable collapsible video player that can be integrated into various results views
 * Location: Results module shared components
 * 
 * Features:
 * - Collapsible header with video metadata
 * - Integration with SharedVideoPlayer
 * - Smooth expand/collapse animations
 * - Loading and error states
 * 
 * Used By:
 * - JobsListView (jobs list from evidence window)
 * - SingleOrderResults (single job results view)
 * - Any other component needing collapsible video functionality
 */

import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { FaChevronUp, FaChevronDown, FaVideo, FaSpinner } from 'react-icons/fa';
import SharedVideoPlayer from '../reportCards/shared/SharedVideoPlayer';
import './CollapsibleVideoPlayer.css';

const CollapsibleVideoPlayer = forwardRef(({
  // Video props
  videoUrl,
  videoTitle = 'Evidence Video',
  videoMetadata = null, // Optional: { duration, size, timestamp, etc. }
  
  // State props
  isLoading = false,
  error = null,
  defaultCollapsed = false,
  autoPlay = false, // Auto-play video when loaded
  
  // Callbacks
  onCollapseChange = null,
  onVideoError = null,
  
  // Style props
  className = '',
  maxHeight = '400px',
  fitMode = 'contain'
}, ref) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const videoRef = useRef(null);

  // Expose video player methods to parent via ref
  useImperativeHandle(ref, () => ({
    seekTo: (seconds) => {
      if (videoRef.current) {
        videoRef.current.seekTo(seconds);
      }
    },
    play: () => {
      if (videoRef.current) {
        videoRef.current.play();
      }
    },
    pause: () => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    },
    getCurrentTime: () => {
      return videoRef.current ? videoRef.current.getCurrentTime() : 0;
    },
    getDuration: () => {
      return videoRef.current ? videoRef.current.getDuration() : 0;
    },
    getVideoElement: () => {
      return videoRef.current ? videoRef.current.getVideoElement() : null;
    }
  }), []);

  const handleToggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    onCollapseChange?.(newState);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  return (
    <div className={`collapsible-video-player ${className}`}>
      {/* Collapsible Header */}
      <div className="video-player-header" onClick={handleToggleCollapse}>
        <div className="video-header-left">
          <FaVideo className="video-icon" />
          <span className="video-title">{videoTitle}</span>
          
          {/* Optional metadata display */}
          {videoMetadata && (
            <span className="video-metadata">
              {videoMetadata.duration && (
                <span className="metadata-item">
                  {formatDuration(videoMetadata.duration)}
                </span>
              )}
              {videoMetadata.size && (
                <span className="metadata-item">
                  {formatFileSize(videoMetadata.size)}
                </span>
              )}
              {videoMetadata.timestamp && (
                <span className="metadata-item">
                  {new Date(videoMetadata.timestamp).toLocaleDateString()}
                </span>
              )}
            </span>
          )}
        </div>

        <button
          className="collapse-toggle-btn"
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? 'Show video' : 'Hide video'}
        >
          {isCollapsed ? (
            <>
              <FaChevronDown />
              <span>Show</span>
            </>
          ) : (
            <>
              <FaChevronUp />
              <span>Hide</span>
            </>
          )}
        </button>
      </div>

      {/* Collapsible Content */}
      <div 
        className={`video-player-content ${isCollapsed ? 'collapsed' : 'expanded'}`}
        style={{ maxHeight: isCollapsed ? '0' : maxHeight }}
      >
        {/* Loading State */}
        {isLoading && !videoUrl && (
          <div className="video-loading-state">
            <FaSpinner className="spinning" />
            <p>Loading video...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="video-error-state">
            <p className="error-message">Failed to load video</p>
            <p className="error-detail">{error}</p>
          </div>
        )}

        {/* Video Player */}
        {videoUrl && !error && (
          <div className="video-player-wrapper">
            <SharedVideoPlayer
              ref={videoRef}
              src={videoUrl}
              fitMode={fitMode}
              autoPlay={autoPlay}
              muted={autoPlay} // Mute if autoplay to ensure browser allows it
              style={{
                width: '100%',
                maxHeight: maxHeight,
                backgroundColor: '#000'
              }}
              onError={(e) => {
                console.error('Video playback error:', e);
                onVideoError?.(e);
              }}
            />
          </div>
        )}

        {/* No Video Available */}
        {!videoUrl && !isLoading && !error && (
          <div className="video-empty-state">
            <FaVideo className="empty-icon" />
            <p>No video available</p>
          </div>
        )}
      </div>
    </div>
  );
});

CollapsibleVideoPlayer.displayName = 'CollapsibleVideoPlayer';

export default CollapsibleVideoPlayer;
